import { useState, useCallback, useEffect, useRef } from 'react';
import { ShellPanel, SideNav, SideNavGroup, SideNavItem } from '@origo/origo-ui';
import { MagnifyingGlassIcon, BuildingsIcon, ImagesIcon } from '@phosphor-icons/react';
import { useAppSelector } from '../store/storeHooks';
import { selectEiendomsvelgerFocus } from '../store/uiSlice';
import styles from './AdminSidePanel.module.css';
import type { Arealplan } from '@origo/arealplan';
import { AdminView } from '../features/admin/AdminView';
import { KommunaleSectionsView } from '../features/admin/KommunaleSectionsView';
import { KommunaltGalleriPanel } from '../features/admin/components/kommunalt-galleri/KommunaltGalleriPanel';

export interface PanelComponentProps {
    onClose: () => void;
    onPanelWidthChange?: (width: number) => void;
    eiendommerUrl?: string;
}

type ActivePanel = 'admin' | 'kommunale' | 'galleri';

interface AdminSidePanelProps {
    eiendommerUrl?: string;
}

export function AdminSidePanel({ eiendommerUrl }: Readonly<AdminSidePanelProps> = {}) {
    const [panelWidth, setPanelWidth] = useState(() => Math.floor(window.innerWidth * 0.5));
    const [activePanel, setActivePanel] = useState<ActivePanel>('admin');
    const [selectedPlan, setSelectedPlan] = useState<Arealplan | null>(null);
    // Skjul nav-rail i map-first fokus-modus (eiendomsvelgeren).
    const eiendomsvelgerFocus = useAppSelector(selectEiendomsvelgerFocus);

    // Snap til 50/50 når man går inn i eiendomsvelgeren; gjenopprett forrige
    // bredde når man går ut (kartet skaleres tilbake til det det var).
    // ShellPanel animerer selve bredde-endringen via .content-transitionen.
    const widthBeforeFocus = useRef<number | null>(null);
    useEffect(() => {
        if (eiendomsvelgerFocus) {
            widthBeforeFocus.current = panelWidth;
            setPanelWidth(Math.floor(window.innerWidth * 0.5));
        } else if (widthBeforeFocus.current != null) {
            setPanelWidth(widthBeforeFocus.current);
            widthBeforeFocus.current = null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eiendomsvelgerFocus]);

    const handleWidthChange = useCallback((width: number) => {
        setPanelWidth(width);
    }, []);

    return (
        <ShellPanel
            actionBar={
                <div className={`${styles.railCollapser}${eiendomsvelgerFocus ? ' ' + styles.railCollapserClosed : ''}`}>
                <SideNav
                    activeId={activePanel}
                    onSelect={(id) => setActivePanel(id as ActivePanel)}
                    collapsed
                >
                    <SideNavGroup>
                        <SideNavItem
                            id="admin"
                            icon={<MagnifyingGlassIcon size={24} weight="duotone" />}
                            description="Søk opp plan, eiendom eller sak."
                        >
                            Plansøk
                        </SideNavItem>
                        <SideNavItem
                            id="kommunale"
                            icon={<BuildingsIcon size={24} weight="duotone" />}
                            description="Rediger eller legg til veiledninger som vises til innbyggere for hele kommunen."
                        >
                            Generelle veiledningspunkt
                        </SideNavItem>
                        <SideNavItem
                            id="galleri"
                            icon={<ImagesIcon size={24} weight="duotone" />}
                            description="Se og rediger illustrasjoner i det kommunale galleriet."
                        >
                            Kommunalt galleri
                        </SideNavItem>
                    </SideNavGroup>
                </SideNav>
                </div>
            }
            resizable
            defaultWidth={panelWidth}
            minWidthVw={30}
            maxWidthVw={75}
        >
            {activePanel === 'admin'
                ? <AdminView onClose={() => {}} onPanelWidthChange={handleWidthChange} eiendommerUrl={eiendommerUrl} selectedPlan={selectedPlan} onSelectPlan={setSelectedPlan} />
                : activePanel === 'kommunale'
                ? <KommunaleSectionsView onClose={() => {}} onPanelWidthChange={handleWidthChange} />
                : <KommunaltGalleriPanel />
            }
        </ShellPanel>
    );
}
